
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const PortfolioPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-teal-600 to-blue-700">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Investment Portfolio</h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto leading-relaxed">
            Explore our diverse portfolio of successful glamping and tiny home developments across premium natural locations
          </p>
        </div>
      </section>

      {/* Portfolio Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">$45M+</div>
              <div className="text-gray-600">Total Portfolio Value</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">28</div>
              <div className="text-gray-600">Active Properties</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">22%</div>
              <div className="text-gray-600">Average Annual ROI</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">95%</div>
              <div className="text-gray-600">Occupancy Rate</div>
            </div>
          </div>
          
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Investment Portfolio</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A carefully curated collection of premium glamping and tiny home developments delivering consistent returns and sustainable growth
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Properties */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mountain Ridge Resort */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://public.readdy.ai/ai/img_res/b3b5e1c2b106c94ddd455350c2ab1aaa.jpg"
                alt="Mountain Ridge Resort"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mountain Ridge Resort</h3>
                <p className="text-sm text-gray-600 mb-3">Colorado • Operational since 2019</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Premium mountain glamping with 12 luxury tents offering panoramic alpine views and year-round operations.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 24%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">98%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Forest Haven Retreat */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Successful%20forest%20glamping%20retreat%20with%20luxury%20treehouses%20and%20elevated%20cabins%2C%20dense%20woodland%20setting%2C%20premium%20eco-accommodation%2C%20high-end%20hospitality%20development%20with%20excellent%20guest%20reviews&width=400&height=250&seq=forest-portfolio&orientation=landscape"
                alt="Forest Haven Retreat"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Forest Haven Retreat</h3>
                <p className="text-sm text-gray-600 mb-3">Oregon • Operational since 2020</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Elevated treehouses and forest cabins providing immersive woodland experiences with luxury amenities.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 21%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">94%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lakeside Luxury Lodge */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Premium%20lakeside%20glamping%20resort%20with%20floating%20cabins%20and%20waterfront%20accommodations%2C%20crystal%20clear%20lake%20with%20mountain%20backdrop%2C%20successful%20luxury%20hospitality%20development%2C%20high-end%20guest%20amenities%20and%20services&width=400&height=250&seq=lakeside-portfolio&orientation=landscape"
                alt="Lakeside Luxury Lodge"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lakeside Luxury Lodge</h3>
                <p className="text-sm text-gray-600 mb-3">Montana • Operational since 2018</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Waterfront glamping with floating cabins and private docks, offering premium lake experiences.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 26%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">97%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desert Oasis Resort */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20desert%20glamping%20resort%20with%20climate-controlled%20tents%2C%20red%20rock%20formations%20backdrop%2C%20successful%20southwestern%20hospitality%20development%2C%20premium%20stargazing%20amenities%20and%20desert%20adventure%20activities&width=400&height=250&seq=desert-portfolio&orientation=landscape"
                alt="Desert Oasis Resort"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Desert Oasis Resort</h3>
                <p className="text-sm text-gray-600 mb-3">Arizona • Operational since 2021</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Climate-controlled desert glamping with world-class stargazing and unique southwestern experiences.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 19%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">92%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coastal Retreat Village */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Successful%20coastal%20glamping%20village%20with%20luxury%20beach%20accommodations%2C%20ocean%20views%20and%20sandy%20beaches%2C%20premium%20seaside%20hospitality%20development%2C%20high-end%20beachfront%20amenities%20and%20water%20activities&width=400&height=250&seq=coastal-portfolio&orientation=landscape"
                alt="Coastal Retreat Village"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Coastal Retreat Village</h3>
                <p className="text-sm text-gray-600 mb-3">California • Operational since 2020</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Beachfront glamping village with ocean views and direct beach access for premium coastal experiences.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 23%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">96%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Alpine Tiny Home Community */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=Premium%20tiny%20home%20community%20in%20alpine%20setting%2C%20modern%20sustainable%20micro-homes%20with%20mountain%20views%2C%20successful%20alternative%20housing%20development%2C%20eco-friendly%20village%20with%20shared%20amenities%20and%20natural%20landscaping&width=400&height=250&seq=tiny-home-portfolio&orientation=landscape"
                alt="Alpine Tiny Home Community"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Alpine Tiny Home Community</h3>
                <p className="text-sm text-gray-600 mb-3">Utah • Operational since 2022</p>
                <p className="text-gray-700 mb-4 text-sm">
                  Sustainable tiny home community offering long-term rentals and vacation stays in alpine setting.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-teal-600">ROI: 20%</div>
                    <div className="text-gray-600">Annual Return</div>
                  </div>
                  <div>
                    <div className="font-semibold text-teal-600">93%</div>
                    <div className="text-gray-600">Occupancy</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Portfolio Performance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Consistent growth and strong returns across all property types and locations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Revenue Growth</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2019</span>
                  <span className="font-semibold">$2.1M</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2020</span>
                  <span className="font-semibold">$3.8M</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2021</span>
                  <span className="font-semibold">$6.2M</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2022</span>
                  <span className="font-semibold">$8.9M</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">2023</span>
                  <span className="font-semibold">$12.4M</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Metrics</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Average Occupancy Rate</span>
                    <span className="font-semibold">95%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-600 h-2 rounded-full" style={{width: '95%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Guest Satisfaction</span>
                    <span className="font-semibold">4.8/5</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-600 h-2 rounded-full" style={{width: '96%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Return Guest Rate</span>
                    <span className="font-semibold">68%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-600 h-2 rounded-full" style={{width: '68%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-teal-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Join Our Success Story</h2>
          <p className="text-xl text-teal-100 mb-8 leading-relaxed">
            Invest in our proven portfolio and become part of the growing glamping and tiny home revolution
          </p>
          <Link
            to="/start-investing"
            className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Investing Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
